import React from "react";
import { Box, Text } from "@mantine/core";
import {
  ReportMetricDef,
  ReportV2Definition,
  ViewConfig,
} from "../../../../types/reportsV2";
import { ViewPatch } from "../hooks/useReportDraft";
import ArrowReorderList from "./ArrowReorderList";
import { toggleKpi } from "./customizeRules";
import MetricChecklist from "./MetricChecklist";
import SortableList from "./SortableList";
import styles from "./CustomizeDrawer.module.css";

export interface TotalsTabProps {
  definition: ReportV2Definition;
  catalog: ReportMetricDef[];
  view: ViewConfig;
  onUpdate: (patch: ViewPatch) => void;
  /** Phones: reorder with arrow buttons instead of dragging. */
  mobile?: boolean;
}

/**
 * Summary totals: the chosen 1–4 metrics (the definition's kpi_limits) in
 * display order (drag, or the arrows on a phone, to reorder), then every
 * metric to pick from; the rest are disabled once the maximum is reached.
 */
export default function TotalsTab({
  definition,
  catalog,
  view,
  onUpdate,
  mobile = false,
}: TotalsTabProps) {
  const ReorderList = mobile ? ArrowReorderList : SortableList;
  const limits = definition.kpi_limits;
  const labelOf = (key: string) =>
    catalog.find((m) => m.key === key)?.label ?? key;
  const isChecked = (key: string) => view.kpis.includes(key);
  const atMax = view.kpis.length >= limits.max;
  const atMin = view.kpis.length <= limits.min;
  const isDisabled = (m: ReportMetricDef) =>
    isChecked(m.key) ? atMin : atMax || !m.available;

  return (
    <>
      <Text className={styles.note}>
        {mobile ? (
          `Up to ${limits.max} totals show above the cards. Use the arrows to reorder.`
        ) : (
          <>Up to {limits.max} totals show above the table. Drag to reorder.</>
        )}
      </Text>
      <Box className={styles.listHeading}>
        <Text component="span" className={styles.sectionLabel}>
          Selected
        </Text>
        <Text component="span" className={styles.sectionCount}>
          {view.kpis.length} of {limits.max} selected
        </Text>
      </Box>
      <ReorderList
        ariaLabel="Selected totals"
        items={view.kpis.map((key) => ({ id: key, label: labelOf(key) }))}
        canRemove={!atMin}
        onReorder={(kpis) => onUpdate({ kpis })}
        onRemove={(key) =>
          onUpdate((v) => ({ kpis: toggleKpi(v.kpis, key, limits) }))
        }
      />
      <MetricChecklist
        sections={definition.metric_sections}
        metrics={catalog}
        isChecked={isChecked}
        isDisabled={isDisabled}
        onToggle={(key) =>
          onUpdate((v) => ({ kpis: toggleKpi(v.kpis, key, limits) }))
        }
      />
    </>
  );
}
